import {
    Mp2BasicInfoFieldId,
    Mp2ConfigureAlarmsFieldId,
    TRITON_MONITOR_CONFIGURATION_CONSTANTS,
    TritonInfoFieldId,
    TritonSectionIds,
} from './base-config-models';
import {
    AdvancedFieldId,
    ChannelInstallationFieldIds,
    ChannelInstallationMp2FieldIds,
    FlumeInstallationFieldIds,
    FlumeInstallationMp2FieldIds,
    PipeInstallationFieldIds,
    PipeInstallationMp2FieldIds,
    WeirInstallationFieldIds,
    WeirInstallationMp2FieldIds,
} from '../common.models';
import { INSTALLATION_DEFAULT_CONSTANTS } from './installation-types/config-models';
import { DefinedInstallationType, INSTALLATION_TYPE } from 'app/shared/models/location-details';
import { DeviceTypeCode, NonSensorDeviceNames } from 'app/shared/enums/device-type-code';
import { gatherSamplerInfo, getSensorDataByName } from './sensors/api-data';
import { BasicInfoCommonFieldIds, ConfigureAlarmsFieldId, DataDeliveryFieldId, TAssembledApiData } from '../common.models';
import { findFieldById, findSectionById, normalizeSeriesName } from '../base-utils';
import { AutocompleteField, FieldType, SelectField, TextField } from '../../models/field-models';
import { RainGaugeLocation, SensorElement } from '../../../models/location-card.model';
import { TritonConfigurationPayload, TritonLocationPayload } from './base-api-models';
import { EchoEmailRecipient, EchoSmsRecipient, TritonLocationCardState } from './models';
import { LocationStatus } from 'app/shared/models/locations';

export function gatherTritonBasicInfoPayload(state: TritonLocationCardState): TritonLocationPayload[] {
    const result: TritonLocationPayload[] = [];

    let mp1Info: Partial<TritonLocationPayload> = {};
    Object.keys(SaveBasicInfoPayload).forEach((key) => {
        mp1Info[key] = SaveBasicInfoPayload[key](state);
    });

    const installationType = SaveInstallationPayload(state, 1);
    mp1Info = { ...mp1Info, ...installationType };

    result.push(mp1Info as TritonLocationPayload);

    const mp2Section = state.formState.config.sections.find((section) => section.id === TritonSectionIds.Mp2BasicInfo);
    if (mp2Section) {
        let mp2Info: Partial<TritonLocationPayload> = {};
        Object.keys(SaveBasicInfoPayloadMp2).forEach((key) => {
            mp2Info[key] = SaveBasicInfoPayloadMp2[key](state);
        });

        const installationType = SaveInstallationPayload(state, 2);
        mp2Info = { ...mp2Info, ...installationType };

        result.push(mp2Info as TritonLocationPayload);
    } else if (state.mp2LastBasicInfo && state.locationDetails.length > 1) {
        result.push({ ...state.mp2LastBasicInfo, status: LocationStatus.Inactive });
    }

    return result;
}

export function extractMp2Payload(state: TritonLocationCardState): TritonLocationPayload | null {
    const mp2Section = state.formState.config.sections.find((section) => section.id === TritonSectionIds.Mp2BasicInfo);
    if (!mp2Section) {
        return null;
    }

    let mp2Info: Partial<TritonLocationPayload> = {};
    Object.keys(SaveBasicInfoPayloadMp2).forEach((key) => {
        mp2Info[key] = SaveBasicInfoPayloadMp2[key](state);
    });

    const installationType = SaveInstallationPayload(state, 2);
    mp2Info = { ...mp2Info, ...installationType };

    return mp2Info as TritonLocationPayload;
}

export const SaveBasicInfoPayload: { [key: string]: (state: TritonLocationCardState) => TAssembledApiData } = {
    locationName: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.LocationName).value,
    ipaddress: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.IpAddress).value,
    assignedRainGaugeId: (state) => {
        const rainGaugeField = findFieldById(state.formState, BasicInfoCommonFieldIds.AssignedRainGauge) as SelectField<RainGaugeLocation, string>;
        const rainGauge = rainGaugeField.value;

        if (!rainGauge) {
            return null;
        }

        const assignedRainGauge = state.options.rainGauges.data.find((rg) => rg.locationName === rainGauge);

        if (!assignedRainGauge) {
            return null;
        }

        return assignedRainGauge.locationId;
    },
    communicationType: (state) =>
        Number(findFieldById(state.formState, BasicInfoCommonFieldIds.CommunicationType).value),
    description: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Description).value,
    installationDate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.InstallationDate).value,
    latitude: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Latitude).value,
    longitude: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Longitude).value,
    manholeaddress: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ManholeAddress).value,
    manholedepth: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ManholeDepth).value,
    qFinalEntityID: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.QFinalEntityID).value,
    serialnumber: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SerialNumber).value,
    series: (state) => normalizeSeriesName(findFieldById(state.formState, BasicInfoCommonFieldIds.Series).value as string),
    status: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Status).value,
    maintenanceInterval: (state) => {
        const status = findFieldById(state.formState, BasicInfoCommonFieldIds.Status).value;
        return status === LocationStatus.Maintenance
            ? findFieldById(state.formState, BasicInfoCommonFieldIds.MaintenanceInterval)?.value ?? null
            : null;
    },
    apnProvider: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ApnProvider).value,
    samplerate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SampleRate).value,
    fastrate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.FastRate).value,
    scanRate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ScanRate).value,
    usedaylightsaving: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.UseDaylightSaving).value,
    synctime: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SyncTime).value,
    customProperties: (state) => {
        const customPropertiesSection = findSectionById(
            state.formState,
            BasicInfoCommonFieldIds.CustomPropertiesSection,
        );
        const customProperties: { key: string; value: string }[] = [];

        let i = 0;
        while (true) {
            if (customPropertiesSection!.fields[`${BasicInfoCommonFieldIds.CustomPropertyKey}-${i}`] === undefined) {
                break;
            }

            const keyField = customPropertiesSection!.fields[`${BasicInfoCommonFieldIds.CustomPropertyKey}-${i}`] as AutocompleteField<string, string>;
            const valueField = customPropertiesSection!.fields[`${BasicInfoCommonFieldIds.CustomPropertyValue}-${i}`] as TextField;

            customProperties.push({
                key: keyField.value,
                value: valueField.value,
            });

            i++;
        }

        return customProperties;
    },
};

export const SaveBasicInfoPayloadMp2: { [key: string]: (state: TritonLocationCardState) => TAssembledApiData } = {
    communicationType: (state) =>
        Number(findFieldById(state.formState, BasicInfoCommonFieldIds.CommunicationType).value),
    description: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Description).value,
    installationDate: (state) => findFieldById(state.formState, Mp2BasicInfoFieldId.InstallationDate).value,
    latitude: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Latitude).value,
    longitude: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Longitude).value,
    locationName: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.LocationName).value + '(2)',
    qFinalEntityID: (state) => findFieldById(state.formState, Mp2BasicInfoFieldId.QFinalEntityID).value,
    serialnumber: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SerialNumber).value,
    assignedRainGaugeId: (state) => {
        const rainGaugeField = findFieldById(state.formState, Mp2BasicInfoFieldId.AssignedRainGauge) as SelectField<RainGaugeLocation, string>;
        const rainGauge = rainGaugeField.value;

        if (!rainGauge) {
            return null;
        }

        const assignedRainGauge = state.options.rainGauges.data.find((rg) => rg.locationName === rainGauge);

        if (!assignedRainGauge) {
            return null;
        }

        return assignedRainGauge.locationId;
    },
    series: (state) => normalizeSeriesName(findFieldById(state.formState, BasicInfoCommonFieldIds.Series).value as string),
    status: (state) =>
        (findFieldById(state.formState, Mp2BasicInfoFieldId.Status)
            ?? findFieldById(state.formState, BasicInfoCommonFieldIds.Status))?.value,
    maintenanceInterval: (state) => {
        const status = (findFieldById(state.formState, Mp2BasicInfoFieldId.Status)
            ?? findFieldById(state.formState, BasicInfoCommonFieldIds.Status))?.value;
        return status === LocationStatus.Maintenance
            ? findFieldById(state.formState, Mp2BasicInfoFieldId.MaintenanceInterval)?.value ?? null
            : null;
    },
    customProperties: (state) => {
        const customPropertiesSection = findSectionById(state.formState, Mp2BasicInfoFieldId.CustomPropertiesSection);
        const customProperties = [];

        let i = 0;
        while (true) {
            if (customPropertiesSection.fields[`${Mp2BasicInfoFieldId.CustomPropertyKey}-${i}`] === undefined) {
                break;
            }

            customProperties.push({
                key: customPropertiesSection.fields[`${Mp2BasicInfoFieldId.CustomPropertyKey}-${i}`].value,
                value: customPropertiesSection.fields[`${Mp2BasicInfoFieldId.CustomPropertyValue}-${i}`].value,
            });

            i++;
        }

        return customProperties;
    },
};

export const SaveInstallationPayload = (state: TritonLocationCardState, mp: 1 | 2) => {
    const basicInfo = state.formState.config.sections.find((s) => s.id === TritonSectionIds.BasicInfo);
    const mp2basicInfo = state.formState.config.sections.find((s) => s.id === TritonSectionIds.Mp2BasicInfo);

    const installationType =
(        mp === 1
            ? basicInfo?.fields[BasicInfoCommonFieldIds.InstallationType].value
            : mp2basicInfo?.fields[Mp2BasicInfoFieldId.InstallationType].value) as string;

    if (!installationType) {
        console.error('Installation type not found');
        return null;
    }

    const depthSensor1 = findFieldById(state.formState, TritonInfoFieldId.DepthSensor1).value;
    const velocitySensor1 = findFieldById(state.formState, TritonInfoFieldId.VelocitySensor1).value;

    const depthSensor2 = findFieldById(state.formState, TritonInfoFieldId.DepthSensor2).value;
    const velocitySensor2 = findFieldById(state.formState, TritonInfoFieldId.VelocitySensor2).value;

    const [type, shape] = installationType.split(':').map((s) => s.trim());

    if (type === INSTALLATION_TYPE.PIPE) {
        const FieldIds = mp === 1 ? PipeInstallationFieldIds : PipeInstallationMp2FieldIds;
        return {
            pipe: {
                height: findFieldById(state.formState, FieldIds.Height).value,
                width: findFieldById(state.formState, FieldIds.Width).value,
                lookupDesc: type,
                hc: findFieldById(state.formState, FieldIds.Hc).value,
                roughness: findFieldById(state.formState, FieldIds.Roughness).value,
                effectiveRoughness: findFieldById(state.formState, FieldIds.EffectiveRoughness).value,
                slope: findFieldById(state.formState, FieldIds.Slope).value,
                frictionFactor: findFieldById(state.formState, FieldIds.FrictionFactor).value,
                nominalDiameter: findFieldById(state.formState, FieldIds.NominalDiameter).value,
                startDate: findFieldById(state.formState, FieldIds.StartDate).value,
                installationType: type,
                MonitoringPoint: mp,
                installationID: DefinedInstallationType.Pipe,
                shapeType: shape,
                installationShapeType: shape,
                depthSensor: mp === 1 ? depthSensor1 : depthSensor2,
                velocitySensor: mp === 1 ? velocitySensor1 : velocitySensor2,
                ...INSTALLATION_DEFAULT_CONSTANTS,
            },
            installationID: DefinedInstallationType.Pipe,
            installationtypeid: DefinedInstallationType.Pipe,
            installationShapeType: shape,
        };
    }

    if (type === INSTALLATION_TYPE.WEIR) {
        const FieldIds = mp === 1 ? WeirInstallationFieldIds : WeirInstallationMp2FieldIds;

        return {
            weir: {
                range: findFieldById(state.formState, FieldIds.Range).value,
                capacity: findFieldById(state.formState, FieldIds.Capacity).value,
                height: findFieldById(state.formState, FieldIds.Height).value,
                coefficient: findFieldById(state.formState, FieldIds.Coefficient).value,
                length: findFieldById(state.formState, FieldIds.Length).value,
                breadth: findFieldById(state.formState, FieldIds.Breadth).value,
                lookupDesc: type,
                installationType: type,
                MonitoringPoint: mp,
                installationID: DefinedInstallationType.Weir,
                shapeType: shape,
                installationShapeType: shape,
                ...INSTALLATION_DEFAULT_CONSTANTS,
                depthSensor: mp === 1 ? depthSensor1 : depthSensor2,
                velocitySensor: mp === 1 ? velocitySensor1 : velocitySensor2,
            },
            installationID: DefinedInstallationType.Weir,
            installationtypeid: DefinedInstallationType.Weir,
            installationShapeType: shape,
        };
    }

    if (type === INSTALLATION_TYPE.FLUME) {
        const FieldIds = mp === 1 ? FlumeInstallationFieldIds : FlumeInstallationMp2FieldIds;

        return {
            flume: {
                range: findFieldById(state.formState, FieldIds.Range).value,
                capacity: findFieldById(state.formState, FieldIds.Capacity).value,
                width: findFieldById(state.formState, FieldIds.Width).value,
                lookupDesc: type,
                installationType: type,
                MonitoringPoint: mp,
                installationID: DefinedInstallationType.Flume,
                shapeType: shape,
                installationShapeType: shape,
                depthSensor: mp === 1 ? depthSensor1 : depthSensor2,
                velocitySensor: mp === 1 ? velocitySensor1 : velocitySensor2,
                ...INSTALLATION_DEFAULT_CONSTANTS,
            },
            installationID: DefinedInstallationType.Flume,
            installationtypeid: DefinedInstallationType.Flume,
            installationShapeType: shape,
        };
    }

    if (type === INSTALLATION_TYPE.CHANNEL) {
        const FieldIds = mp === 1 ? ChannelInstallationFieldIds : ChannelInstallationMp2FieldIds;

        const entriesSection = findSectionById(state.formState, FieldIds.ArraySection);
        const numberOfEntriesField = findFieldById(state.formState, FieldIds.NumberOfEntries, FieldType.Number);
        const rangeField = findFieldById(state.formState, FieldIds.Range, FieldType.Number);

        if (!entriesSection || !numberOfEntriesField || !rangeField) {
            console.error('Channel installation fields not found');
            return null;
        }

        const numberOfEntries = numberOfEntriesField.value;
        const entries: { depth: number; quantity: number }[] = [];

        for (let i = 0; i < Number(numberOfEntries); i++) {
            const depthField = entriesSection.fields[`${FieldIds.Depth}-${i}`];
            const quantityField = entriesSection.fields[`${FieldIds.Quantity}-${i}`];

            if (depthField?.type !== FieldType.Number || quantityField?.type !== FieldType.Number) {
                console.error('Channel installation entry fields not found');
                return null;
            }

            entries.push({
                depth: depthField.value,
                quantity: quantityField.value,
            });
        }

        return {
            channel: {
                range: rangeField.value,
                numberOfEntries: numberOfEntries,
                entries: entries,
                lookupDesc: type,
                installationType: type,
                MonitoringPoint: mp,
                installationID: DefinedInstallationType.Channel,
                shapeType: shape,
                installationShapeType: shape,
                depthSensor: mp === 1 ? depthSensor1 : depthSensor2,
                velocitySensor: mp === 1 ? velocitySensor1 : velocitySensor2,
                ...INSTALLATION_DEFAULT_CONSTANTS,
            },
            installationID: DefinedInstallationType.Channel,
            installationtypeid: DefinedInstallationType.Channel,
            installationShapeType: shape,
        };
    }

    console.error('Installation type not supported');
    return null;
};

export function gatherTritonConfigurationInfo(state: TritonLocationCardState): TritonConfigurationPayload {
    const tritonInfoSection = state.formState.config.sections.find((s) => s.id === TritonSectionIds.TritonInfo);
    const sensor1Field = tritonInfoSection.fields[TritonInfoFieldId.Sensor1] as SelectField<SensorElement, string>;
    const sensor2Field = tritonInfoSection.fields[TritonInfoFieldId.Sensor2] as SelectField<SensorElement, string>;

    const devices = [];

    const alarm1Info = gatherConfigureAlarmsFirst(state);
    const alarm2Info = gatherConfigureAlarmsSecond(state);
    const samplerInfo = gatherSamplerInfo(state.formState);

    const sensor1 = getSensorDataByName(sensor1Field.value, state.formState, 1);
    const sensor2 = getSensorDataByName(sensor2Field.value, state.formState, 2);

    devices.push(...alarm1Info, ...alarm2Info, sensor1, sensor2, samplerInfo);

    const silt1 = findFieldById(state.formState, TritonInfoFieldId.Silt1, FieldType.Number);
    const silt2 = findFieldById(state.formState, TritonInfoFieldId.Silt2, FieldType.Number);

    const emailRec = [1,2,3,4,5].flatMap(i =>
        buildEmailRecipient(state, AdvancedFieldId[`CheckInEmailRecipient${i}`])
    );
    const smsRec = [1,2,3,4,5].flatMap(i =>
        buildSmsRecipient(state, {
            recipient: AdvancedFieldId[`CheckInTextRecipient${i}`],
            startHour: AdvancedFieldId[`CheckInTextRec${i}StartHour`],
            endHour: AdvancedFieldId[`CheckInTextRec${i}EndHour`],
        })
    );

    const result: { [key: string]: (state: TritonLocationCardState) => TAssembledApiData } = {
        prismdeliveryipaddress: (state) =>
            findFieldById(state.formState, DataDeliveryFieldId.PrismDeliveryIPAddress).value,
        prismdeliveryfast: (state) => findFieldById(state.formState, DataDeliveryFieldId.PrismDeliveryFast).value,
        prismdeliverynormal: (state) => findFieldById(state.formState, DataDeliveryFieldId.PrismDeliveryNormal).value,
        devices: () => devices.filter((v) => !!v),
        silts: () => [silt1?.value ?? null, silt2?.value ?? null].filter((v): v is number => v !== null),
        samplerate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SampleRate).value,
        usedaylightsaving: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.UseDaylightSaving).value,
        synctime: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SyncTime).value,
        apnProvider: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ApnProvider).value,
        apn: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ApnProvider).value,
        fastrate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.FastRate).value,
        scanRate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ScanRate).value,
        lowLevelEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.LowLevelEnable).value,
        lowLevelThreshold: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.LowLevelThreshold).value,
        fullPipeEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.FullPipeEnable).value,
        highLevelEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighLevelEnable).value,
        highLevelThreshold: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighLevelThreshold).value,
        highHighEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighHighEnable).value,
        highHighThreshold: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighHighThreshold).value,
        highFlowEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighFlowEnable).value,
        highFlowThreshold: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighFlowThreshold).value,
        overflowEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.OverflowEnable).value,
        batteryLowEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.BatteryLowEnable).value,
        batteryLowThreshold: (state) =>
            findFieldById(state.formState, ConfigureAlarmsFieldId.BatteryLowThreshold).value,
        storetemperature: (state) => findFieldById(state.formState, TritonInfoFieldId.StoreTemperature).value,
        storebattery: (state) => findFieldById(state.formState, TritonInfoFieldId.BatteryVoltage).value,
        comport: (state) => findFieldById(state.formState, TritonInfoFieldId.ComPort).value,
        ipaddress: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.IpAddress).value,
        inclinometerthreshold: (state) => findFieldById(state.formState, TritonInfoFieldId.TiltAlarmThreshold).value,
        checkinenable: (state) => findFieldById(state.formState, AdvancedFieldId.CheckInEnable).value,
        checkinhour: (state) => findFieldById(state.formState, AdvancedFieldId.CheckInHour).value,
        smsrecipients: () => smsRec.filter((v) => !!v),
        emailrecipients: () => emailRec.filter((v) => !!v)
    };

    const configPayload: Partial<TritonConfigurationPayload> = Object.keys(result).reduce(
        (acc, key) => {
            acc[key] = result[key](state);
            return acc;
        },
        { ...TRITON_MONITOR_CONFIGURATION_CONSTANTS },
    );

    return configPayload as TritonConfigurationPayload;
}

function gatherConfigureAlarmsFirst(state: TritonLocationCardState) {
    const result = [];

    const flowLossAlarm = findFieldById(state.formState, ConfigureAlarmsFieldId.FlowLossAlarm).value;
    const alarmInfo: { [key: string]: (state: TritonLocationCardState) => TAssembledApiData } = {
        flowLossAlarm: () => flowLossAlarm,
        percent: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.Percent).value,
        sensitivity: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.Sensitivity).value,
        flowLossType: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.FlowLossType).value,
        clearPatternHistory: (state) =>
            findFieldById(state.formState, ConfigureAlarmsFieldId.ClearPatternHistory).value,
        fullPipethreshold: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.FullPipeThreshold).value,
        fullPipeEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.FullPipeEnable).value,
        highAlaramreshold: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighHighThreshold).value,
        highHighEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighHighEnable).value,
        highHighThreshold: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighHighThreshold).value,
        highLevelEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighLevelEnable).value,
        highLevelThreshold: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighLevelThreshold).value,
        lowLevelEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.LowLevelEnable).value,
        lowLevelThreshold: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.LowLevelThreshold).value,
        highFlowEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighFlowEnable).value,
        highFlowThreshold: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.HighFlowThreshold).value,
        overflowEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.OverflowEnable).value,
        batteryLowEnable: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.BatteryLowEnable).value,
        batteryLowThreshold: (state) =>
            findFieldById(state.formState, ConfigureAlarmsFieldId.BatteryLowThreshold).value,
        prismdeliveryipaddress: (state) =>
            findFieldById(state.formState, DataDeliveryFieldId.PrismDeliveryIPAddress).value,
        prismdeliveryfast: (state) => findFieldById(state.formState, DataDeliveryFieldId.PrismDeliveryFast).value,
        prismdeliverynormal: (state) => findFieldById(state.formState, DataDeliveryFieldId.PrismDeliveryNormal).value,
        name: () => NonSensorDeviceNames.Alarm1,
        type: () => DeviceTypeCode.EchoAlarm1,
        installationNumber: () => 0,
    };

    const alarmResult = Object.keys(alarmInfo).reduce((acc, key) => {
        acc[key] = alarmInfo[key](state);
        return acc;
    }, {});

    result.push(alarmResult);

    if (flowLossAlarm) {
        const flowLossInfo: { [key: string]: (state: TritonLocationCardState) => TAssembledApiData } = {
            name: () => NonSensorDeviceNames.FlowLoss1,
            active: () => true,
            installationNumber: () => 0,
            type: () => DeviceTypeCode.FlowLoss1,
            clearPatternHistory: (state) =>
                findFieldById(state.formState, ConfigureAlarmsFieldId.ClearPatternHistory).value,
            flowLossType: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.FlowLossType).value,
            percent: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.Percent).value,
            sensitivity: (state) => findFieldById(state.formState, ConfigureAlarmsFieldId.Sensitivity).value,
        };

        const flowLossResult = Object.keys(flowLossInfo).reduce((acc, key) => {
            acc[key] = flowLossInfo[key](state);
            return acc;
        }, {});

        result.push(flowLossResult);
    }

    return result;
}

function gatherConfigureAlarmsSecond(state: TritonLocationCardState) {
    const result = [];

    const alarm2Section = state.formState.config.sections.find((s) => s.id === TritonSectionIds.Mp2Alarms);

    if (!alarm2Section) {
        return result;
    }

    const flowLossAlarm = findFieldById(state.formState, Mp2ConfigureAlarmsFieldId.FlowLossAlarm).value;
    const alarmInfo: { [key: string]: (state: TritonLocationCardState) => TAssembledApiData } = {
        flowLossAlarm: () => flowLossAlarm,
        percent: (state) => findFieldById(state.formState, Mp2ConfigureAlarmsFieldId.Percent).value,
        sensitivity: (state) => findFieldById(state.formState, Mp2ConfigureAlarmsFieldId.Sensitivity).value,
        flowLossType: (state) => findFieldById(state.formState, Mp2ConfigureAlarmsFieldId.FlowLossType).value,
        clearPatternHistory: (state) =>
            findFieldById(state.formState, Mp2ConfigureAlarmsFieldId.ClearPatternHistory).value,
        fullPipethreshold: (state) => findFieldById(state.formState, Mp2ConfigureAlarmsFieldId.FullPipeThreshold).value,
        fullPipeEnable: (state) => findFieldById(state.formState, Mp2ConfigureAlarmsFieldId.FullPipeEnable).value,
        highAlaramreshold: (state) => findFieldById(state.formState, Mp2ConfigureAlarmsFieldId.HighHighThreshold).value,
        highHighEnable: (state) => findFieldById(state.formState, Mp2ConfigureAlarmsFieldId.HighHighEnable).value,
        highHighThreshold: (state) => findFieldById(state.formState, Mp2ConfigureAlarmsFieldId.HighHighThreshold).value,
        highLevelEnable: (state) => findFieldById(state.formState, Mp2ConfigureAlarmsFieldId.HighLevelEnable).value,
        highLevelThreshold: (state) =>
            findFieldById(state.formState, Mp2ConfigureAlarmsFieldId.HighLevelThreshold).value,
        lowLevelEnable: (state) => findFieldById(state.formState, Mp2ConfigureAlarmsFieldId.LowLevelEnable).value,
        lowLevelThreshold: (state) => findFieldById(state.formState, Mp2ConfigureAlarmsFieldId.LowLevelThreshold).value,
        highFlowEnable: (state) => findFieldById(state.formState, Mp2ConfigureAlarmsFieldId.HighFlowEnable).value,
        highFlowThreshold: (state) => findFieldById(state.formState, Mp2ConfigureAlarmsFieldId.HighFlowThreshold).value,
        overflowEnable: (state) => findFieldById(state.formState, Mp2ConfigureAlarmsFieldId.OverflowEnable).value,
        prismdeliveryipaddress: (state) =>
            findFieldById(state.formState, DataDeliveryFieldId.PrismDeliveryIPAddress).value,
        prismdeliveryfast: (state) => findFieldById(state.formState, DataDeliveryFieldId.PrismDeliveryFast).value,
        prismdeliverynormal: (state) => findFieldById(state.formState, DataDeliveryFieldId.PrismDeliveryNormal).value,
        name: () => NonSensorDeviceNames.Alarm2,
        type: () => DeviceTypeCode.EchoAlarm2,
        installationNumber: () => 1,
    };

    const alarmResult = Object.keys(alarmInfo).reduce((acc, key) => {
        acc[key] = alarmInfo[key](state);
        return acc;
    }, {});

    result.push(alarmResult);

    if (flowLossAlarm) {
        const flowLossInfo: { [key: string]: (state: TritonLocationCardState) => TAssembledApiData } = {
            name: () => NonSensorDeviceNames.FlowLoss2,
            active: () => true,
            installationNumber: () => 1,
            type: () => DeviceTypeCode.FlowLoss2,
            clearPatternHistory: (state) =>
                findFieldById(state.formState, Mp2ConfigureAlarmsFieldId.ClearPatternHistory).value,
            flowLossType: (state) => findFieldById(state.formState, Mp2ConfigureAlarmsFieldId.FlowLossType).value,
            percent: (state) => findFieldById(state.formState, Mp2ConfigureAlarmsFieldId.Percent).value,
            sensitivity: (state) => findFieldById(state.formState, Mp2ConfigureAlarmsFieldId.Sensitivity).value,
        };

        const flowLossResult = Object.keys(flowLossInfo).reduce((acc, key) => {
            acc[key] = flowLossInfo[key](state);
            return acc;
        }, {});

        result.push(flowLossResult);
    }

    return result;
}


function buildSmsRecipient(state: TritonLocationCardState,ids: {
        recipient: AdvancedFieldId;
        startHour: AdvancedFieldId;
        endHour: AdvancedFieldId;
    }
): EchoSmsRecipient[] {

    return [{
        recipient: findFieldById(state.formState, ids.recipient).value as string,
        startHour: Number(findFieldById(state.formState, ids.startHour).value),
        endHour: Number(findFieldById(state.formState, ids.endHour).value),
    }];
}


function buildEmailRecipient(state: TritonLocationCardState,id: AdvancedFieldId): EchoEmailRecipient[] {
        return [{
            recipient: findFieldById(state.formState, id).value as string
        }];
}
